import type { Card } from '../../types'
import { motion } from 'framer-motion'
import { Music } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { getCardPadding, getScaledFontSize, getScaledIconSize } from '../../utils/gridLayout'

interface SpotifyCurrentCardProps {
  card: Card
  editMode: boolean
  cellSize: number
}

function getSubdomainFromHostname(hostname: string): string {
  const host = hostname.split(':')[0].toLowerCase()
  if (!host) return ''

  if (host === 'oda.bio' || host === 'www.oda.bio') return ''

  if (host.endsWith('.oda.bio')) {
    const sub = host.slice(0, -'.oda.bio'.length)
    return sub === 'www' ? '' : sub
  }

  return ''
}

export default function SpotifyCurrentCard({ card, editMode, cellSize }: SpotifyCurrentCardProps) {
  const [isConnected, setIsConnected] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const hasLoadedOnceRef = useRef(false)
  const intervalIdRef = useRef<number | null>(null)
  const [track, setTrack] = useState<{
    name: string
    artist: string
    album_art: string | null
    external_url: string | null
  } | null>(null)
  const [playedAt, setPlayedAt] = useState<string | null>(null)

  const padding = getCardPadding(cellSize)
  const titleSize = getScaledFontSize(cellSize, 14)
  const trackNameSize = getScaledFontSize(cellSize, 14)
  const artistSize = getScaledFontSize(cellSize, 12)
  const iconSize = getScaledIconSize(cellSize, 24)
  const albumArtSize = Math.max(48, (cellSize / 24) * 64)

  const formatRelativePlayedAt = (iso: string): string => {
    const ts = Date.parse(iso)
    if (!Number.isFinite(ts)) return ''
    const diffMs = Date.now() - ts
    if (!Number.isFinite(diffMs) || diffMs < 0) return ''
    const sec = Math.floor(diffMs / 1000)
    const min = Math.floor(sec / 60)
    const hr = Math.floor(min / 60)
    const day = Math.floor(hr / 24)

    if (min < 1) return 'şimdi'
    if (min < 60) return `${min} dk.`
    if (hr < 24) return `${hr} sa.`
    return `${day} g.`
  }

  useEffect(() => {
    let mounted = true
    const clearPoll = () => {
      if (intervalIdRef.current) {
        window.clearInterval(intervalIdRef.current)
        intervalIdRef.current = null
      }
    }

    const fetchCurrent = async () => {
      if (!hasLoadedOnceRef.current) setIsLoading(true)
      try {
        const onPublicProfile = !editMode
        const subdomain = onPublicProfile ? getSubdomainFromHostname(window.location.hostname) : ''
        const url = (onPublicProfile && subdomain) ? `/api/public/spotify/current/${subdomain}` : '/api/spotify/current'
        const res = await axios.get(url)
        if (!mounted) return
        setIsConnected(true)
        const nextTrack = res.data?.track ?? null
        const nextPlayedAt = (res.data?.played_at as string | null | undefined) ?? null
        const isPlaying = Boolean(res.data?.is_playing)
        // If Spotify isn't currently playing, keep showing the last known track.
        if (nextTrack) setTrack(nextTrack)
        setPlayedAt(isPlaying ? null : nextPlayedAt)
      } catch (err: any) {
        if (!mounted) return
        const status = err?.response?.status
        if (status === 401 || status === 404) {
          setIsConnected(false)
          setTrack(null)
          setPlayedAt(null)
          clearPoll()
        }
      } finally {
        if (!mounted) return
        setIsLoading(false)
        hasLoadedOnceRef.current = true
      }
    }

    const startPoll = () => {
      clearPoll()
      if (document.visibilityState !== 'visible') return
      intervalIdRef.current = window.setInterval(fetchCurrent, 180000)
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void fetchCurrent()
        startPoll()
      } else {
        clearPoll()
      }
    }

    void fetchCurrent()
    startPoll()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      mounted = false
      document.removeEventListener('visibilitychange', onVisibility)
      clearPoll()
    }
  }, [card.id])

  const aspect = card.grid_width / Math.max(1, card.grid_height)
  const isWide = aspect >= 1.45
  const relativePlayedAt = playedAt ? formatRelativePlayedAt(playedAt) : ''

  return (
    <motion.div
      className="w-full h-full theme-card theme-card-hover overflow-hidden"
      whileHover={{ borderColor: 'rgba(0,0,0,0.3)' }}
    >
      {!isConnected ? (
        <div className="w-full h-full flex flex-col justify-center" style={{ padding: `${padding}px` }}>
          <p className="font-serif opacity-80" style={{ fontSize: `${titleSize}px` }}>Spotify bağlı değil</p>
          {editMode && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                window.location.href = '/auth/spotify/login'
              }}
              className="font-serif font-bold border border-[var(--surface-border)] bg-[var(--app-bg)] text-[var(--app-fg)]"
              style={{ marginTop: `${padding}px`, padding: `${padding * 0.5}px ${padding}px`, fontSize: `${titleSize}px` }}
            >
              Connect Spotify
            </button>
          )}
        </div>
      ) : isLoading ? (
        <div className="w-full h-full flex flex-col items-center justify-center" style={{ padding: `${padding}px`, gap: `${padding * 0.5}px` }}>
          <p className="font-serif opacity-70" style={{ fontSize: `${titleSize}px` }}>Loading…</p>
        </div>
      ) : isWide ? (
        <div className="w-full h-full flex items-center" style={{ padding: `${padding}px`, gap: `${padding}px` }}>
          <div className="bg-[var(--app-bg)]/10 flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ width: `${albumArtSize}px`, height: `${albumArtSize}px`, borderRadius: 'var(--card-radius, 0px)' }}>
            {track?.album_art ? (
              <img src={track.album_art ?? undefined} alt="" className="w-full h-full object-cover" />
            ) : (
              <Music size={iconSize} className="opacity-70" />
            )}
          </div>

          <div className="flex-1 min-w-0 flex flex-col justify-center">
            <p className="font-serif font-bold truncate" style={{ fontSize: `${trackNameSize}px` }}>{track?.name || '—'}</p>
            <p className="font-serif truncate opacity-70" style={{ fontSize: `${artistSize}px` }}>{track?.artist || '—'}</p>
          </div>
          {relativePlayedAt && (
            <span className="font-serif opacity-60 flex-shrink-0" style={{ fontSize: `${artistSize}px` }}>{relativePlayedAt}</span>
          )}
        </div>
      ) : (
        <div className="w-full h-full flex flex-col">
          <div className="bg-[var(--app-bg)]/10 flex items-center justify-center overflow-hidden" style={{ width: `${albumArtSize * 1.5}px`, height: `${albumArtSize * 1.5}px`, borderRadius: 'var(--card-radius, 0px)' }}>
            {track?.album_art ? (
              <img src={track.album_art ?? undefined} alt="" className="w-full h-full object-cover" />
            ) : (
              <Music size={iconSize * 1.3} className="opacity-70" />
            )}
          </div>
          <div className="p-3 border-t border-[var(--card-border)]">
            <div className="text-center">
              <div className="flex items-baseline justify-center gap-2">
                <p className="font-serif font-bold" style={{ fontSize: `${trackNameSize}px` }}>{track?.name || '—'}</p>
                {relativePlayedAt && (
                  <span className="font-serif opacity-60" style={{ fontSize: `${artistSize}px` }}>{relativePlayedAt}</span>
                )}
              </div>
              <p className="font-serif opacity-70" style={{ fontSize: `${artistSize}px` }}>{track?.artist || '—'}</p>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  )
}
